import { capitalize } from "@/lib/utils";

export interface ContactSignalsResponse {
  configured: boolean;
  email: string;
  days: number;
  funnel: {
    paywall_shown: number;
    paywall_hit: number;
    upgrade_clicked: number;
    begin_checkout: number;
    checkout_started: number;
    checkout_success: number;
    checkout_canceled: number;
    last_paywall_at: string | null;
  };
  activation: {
    onboarding_complete: number;
    profile_completed: number;
    first_pictogram_placed: number;
    first_wall_drawn: number;
    plan_created: number;
  };
  friction: {
    rageclick: number;
    dead_click: number;
    exception: number;
    login_failed: number;
    checkout_canceled: number;
  };
  timeline: { event: string; timestamp: string }[];
}

type Funnel = ContactSignalsResponse["funnel"];
type Activation = ContactSignalsResponse["activation"];
type Friction = ContactSignalsResponse["friction"];

export function purchaseStage(f: Funnel): { key: string; label: string } {
  if (f.checkout_success > 0) return { key: "converted", label: "Converti" };
  if (
    (f.checkout_started > 0 || f.begin_checkout > 0) &&
    f.checkout_canceled > 0
  )
    return { key: "checkout_abandoned", label: "Checkout abandonné" };
  if (f.begin_checkout > 0 || f.checkout_started > 0)
    return { key: "checkout_started", label: "Checkout entamé" };
  if (f.upgrade_clicked > 0)
    return { key: "upgrade_intent", label: "Intention d'upgrade" };
  if (f.paywall_shown > 0 || f.paywall_hit > 0)
    return { key: "paywall_seen", label: "Paywall vu" };
  return { key: "none", label: "Aucun signal d'achat" };
}

export function activationStatus(a: Activation): {
  key: string;
  label: string;
} {
  if (a.plan_created > 0) return { key: "activated", label: "Activé" };
  if (a.first_pictogram_placed > 0 || a.first_wall_drawn > 0)
    return { key: "creating", label: "En création" };
  if (a.onboarding_complete > 0 || a.profile_completed > 0)
    return { key: "onboarding", label: "Onboardé" };
  return { key: "registered", label: "Inscrit" };
}

export function frictionLevel(fr: Friction): {
  key: "none" | "some" | "high";
  label: string;
} {
  if (fr.exception > 0 || fr.login_failed >= 2 || fr.rageclick >= 3)
    return { key: "high", label: "Friction élevée" };
  if (
    fr.rageclick > 0 ||
    fr.dead_click > 0 ||
    fr.login_failed > 0 ||
    fr.checkout_canceled > 0
  )
    return { key: "some", label: "Quelques frictions" };
  return { key: "none", label: "Aucune friction" };
}

const EVENT_LABELS: Record<string, string> = {
  sign_up: "Inscription",
  login: "Connexion",
  login_failed: "Échec de connexion",
  onboarding_complete: "Onboarding terminé",
  profile_completed: "Profil complété",
  plan_created: "Plan créé",
  first_pictogram_placed: "1ᵉʳ pictogramme placé",
  first_wall_drawn: "1ᵉʳ mur dessiné",
  import_completed: "Import terminé",
  export_completed: "Export terminé",
  paywall_shown: "Paywall vu",
  upgrade_clicked: "Upgrade cliqué",
  begin_checkout: "Checkout démarré",
  checkout_started: "Checkout démarré",
  checkout_success: "Conversion",
  checkout_canceled: "Checkout annulé",
};

export function eventLabel(event: string): string {
  const known = EVENT_LABELS[event];
  if (known) return known;
  const cleaned = event.replace(/^\$/, "").replace(/_/g, " ");
  return capitalize(cleaned);
}
